import {
    IClientFunnelData,
    IPyramidLevel,
    IConversionStep,
} from '../../../../common';
import { REPORTS_COLORS } from '../../../../common/consts/reports.consts';

export const isJobView = (data: IClientFunnelData | null): boolean => {
    return (
        data?.view === 'job' &&
        Array.isArray(data?.steps) &&
        data.steps.length > 0
    );
};

export const buildCompanyPyramidLevels = (
    data: IClientFunnelData
): IPyramidLevel[] => {
    const inProgress =
        data.funnel.find((item) => item.label === 'In Progress')?.count ?? 0;
    const hired =
        data.funnel.find((item) => item.label === 'Hired')?.count ?? 0;

    return [
        {
            label: 'Total Applicants',
            labelKey: 'reportsPage.stageConversionRates.totalApplicants',
            count: data.total,
            widthPercent: 100,
            clipInset: 10,
        },
        {
            label: 'In Progress',
            labelKey: 'reportsPage.stageConversionRates.inProgress',
            count: inProgress,
            widthPercent: 75,
            clipInset: 15,
        },
        {
            label: 'Hired',
            labelKey: 'reportsPage.stageConversionRates.hired',
            count: hired,
            widthPercent: 50,
            clipInset: 20,
        },
    ];
};

const calculatePyramidWidth = (index: number, totalLevels: number): number => {
    if (totalLevels <= 1) return 100;
    return 100 - (index / (totalLevels - 1)) * 75;
};

const calculateClipInset = (index: number, totalLevels: number): number => {
    if (totalLevels <= 1) return 10;
    return 10 + (index / (totalLevels - 1)) * 15;
};

export const buildJobPyramidLevels = (
    data: IClientFunnelData
): IPyramidLevel[] => {
    if (!data.steps || data.steps.length === 0) {
        const hired = data.summary?.hired ?? 0;
        return [
            {
                label: 'Total Applicants',
                labelKey: 'reportsPage.stageConversionRates.totalApplicants',
                count: data.total,
                widthPercent: 100,
                clipInset: 10,
            },
            {
                label: 'Hired',
                labelKey: 'reportsPage.stageConversionRates.hired',
                count: hired,
                widthPercent: 50,
                clipInset: 20,
            },
        ];
    }

    const sortedSteps = [...data.steps].sort(
        (a, b) => a.stepIndex - b.stepIndex
    );
    const hired = data.summary?.hired ?? 0;
    const totalLevels = sortedSteps.length + 2;

    const levels: IPyramidLevel[] = [];

    levels.push({
        label: 'Total Applicants',
        labelKey: 'reportsPage.stageConversionRates.totalApplicants',
        count: data.total,
        widthPercent: calculatePyramidWidth(0, totalLevels),
        clipInset: calculateClipInset(0, totalLevels),
    });

    sortedSteps.forEach((step, index) => {
        const remainingCandidates =
            sortedSteps
                .slice(index)
                .reduce((sum, s) => sum + s.inProgress, 0) + hired;

        levels.push({
            label: step.name,
            count: remainingCandidates,
            widthPercent: calculatePyramidWidth(index + 1, totalLevels),
            clipInset: calculateClipInset(index + 1, totalLevels),
        });
    });

    levels.push({
        label: 'Hired',
        labelKey: 'reportsPage.stageConversionRates.hired',
        count: hired,
        widthPercent: calculatePyramidWidth(totalLevels - 1, totalLevels),
        clipInset: calculateClipInset(totalLevels - 1, totalLevels),
    });

    return levels;
};

export const getPyramidColor = (
    index: number,
    totalLevels: number
): string => {
    if (totalLevels <= REPORTS_COLORS.length) {
        return REPORTS_COLORS[index] ?? REPORTS_COLORS[REPORTS_COLORS.length - 1];
    }
    const ratio = index / (totalLevels - 1);
    const colorIndex = Math.floor(ratio * (REPORTS_COLORS.length - 1));
    return REPORTS_COLORS[colorIndex];
};

export const buildCompanyConversions = (
    data: IClientFunnelData
): IConversionStep[] => {
    const inProgress =
        data.funnel.find((item) => item.label === 'In Progress')?.count ?? 0;
    const hired =
        data.funnel.find((item) => item.label === 'Hired')?.count ?? 0;

    return [
        {
            fromLabel: 'New',
            toLabel: 'In Progress',
            fromLabelKey: 'reportsPage.stageConversionRates.new',
            toLabelKey: 'reportsPage.stageConversionRates.inProgress',
            fromCount: data.total,
            toCount: inProgress,
            percentage: data.total > 0 ? (inProgress / data.total) * 100 : 0,
        },
        {
            fromLabel: 'In Progress',
            toLabel: 'Hired',
            fromLabelKey: 'reportsPage.stageConversionRates.inProgress',
            toLabelKey: 'reportsPage.stageConversionRates.hired',
            fromCount: inProgress,
            toCount: hired,
            percentage: inProgress > 0 ? (hired / inProgress) * 100 : 0,
        },
    ];
};

export const buildJobConversions = (
    data: IClientFunnelData
): IConversionStep[] => {
    if (!data.steps || data.steps.length === 0) {
        return [];
    }

    const sortedSteps = [...data.steps].sort(
        (a, b) => a.stepIndex - b.stepIndex
    );
    const hired = data.summary?.hired ?? 0;
    const conversions: IConversionStep[] = [];

    const remainingAtStage = sortedSteps.map((_, index) =>
        sortedSteps
            .slice(index)
            .reduce((sum, s) => sum + s.inProgress, 0) + hired
    );

    for (let i = 0; i < sortedSteps.length - 1; i++) {
        const currentRemaining = remainingAtStage[i];
        const nextRemaining = remainingAtStage[i + 1];

        conversions.push({
            fromLabel: sortedSteps[i].name,
            toLabel: sortedSteps[i + 1].name,
            fromCount: currentRemaining,
            toCount: nextRemaining,
            percentage:
                currentRemaining > 0
                    ? (nextRemaining / currentRemaining) * 100
                    : 0,
        });
    }

    const lastStepRemaining = remainingAtStage[sortedSteps.length - 1];
    conversions.push({
        fromLabel: sortedSteps[sortedSteps.length - 1].name,
        toLabel: 'Hired',
        toLabelKey: 'reportsPage.stageConversionRates.hired',
        fromCount: lastStepRemaining,
        toCount: hired,
        percentage: lastStepRemaining > 0 ? (hired / lastStepRemaining) * 100 : 0,
    });

    return conversions;
};

export const getPyramidLevels = (
    data: IClientFunnelData | null
): IPyramidLevel[] => {
    if (!data) return [];
    return isJobView(data)
        ? buildJobPyramidLevels(data)
        : buildCompanyPyramidLevels(data);
};

export const getConversionSteps = (
    data: IClientFunnelData | null
): IConversionStep[] => {
    if (!data) return [];
    return isJobView(data)
        ? buildJobConversions(data)
        : buildCompanyConversions(data);
};
